import React, { useEffect, useId, useRef, useState } from "react";
import { Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { SquaresFour, Tag } from "@phosphor-icons/react";
import { t, useStoreLanguage, localizeDigits } from "../../features/store-language/i18n.ts";
import { PendingRing } from "../shared/LoadingRing.jsx";
import { type StoreSearchResult } from "./storeSearchService.ts";

interface Props {
  className?: string;
  companyName: string;
  value: string;
  loading: boolean;
  failed: boolean;
  results: StoreSearchResult[];
  onChange: (value: string) => void;
  onSearch: (value: string) => void;
  onSelect: (result: StoreSearchResult) => void;
  placeholder?: string;
  ariaLabel?: string;
  allResultsLabel?: string;
  emptyLabel?: string;
  disabled?: boolean;
  resultDescription?: (result: StoreSearchResult) => string;
  onFocusChange?: (focused: boolean) => void;
}

export default function ShopSearch({
  className = "",
  companyName,
  value,
  loading,
  failed,
  results,
  onChange,
  onSearch,
  onSelect,
  placeholder,
  ariaLabel,
  allResultsLabel,
  emptyLabel,
  disabled = false,
  resultDescription,
  onFocusChange,
}: Props) {
  useStoreLanguage();
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const listRef = useRef<HTMLDivElement>(null);
  const showResults = open && value.trim().length >= 3;

  useEffect(function () {
    setActiveIndex(-1);
  }, [value, results]);

  useEffect(function () {
    listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  function select(result: StoreSearchResult) {
    setOpen(false);
    onSelect(result);
  }

  return (
    <div
      className={`shop-search ${className}`.trim()}
      onFocus={function () { onFocusChange?.(true); }}
      onBlur={function (event) {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setOpen(false);
          onFocusChange?.(false);
        }
      }}
    >
      <Input
        className="shop-search__autocomplete"
        value={value}
        role="combobox"
        aria-label={ariaLabel || t("جست‌وجوی طرح‌ها و کالاها")}
        disabled={disabled}
        aria-autocomplete="list"
        aria-expanded={showResults}
        aria-controls={showResults ? listId : undefined}
        aria-activedescendant={showResults && activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
        autoComplete="off"
        maxLength={200}
        allowClear
        placeholder={placeholder || t("جستجو در {{value0}}", { value0: companyName })}
        prefix={
          <span className="shop-search__icons">
            {loading ? (
              <PendingRing />
            ) : (
              <SearchOutlined />
            )}
          </span>
        }
        onFocus={function () { setOpen(true); }}
        onChange={function (event) {
          setOpen(true);
          onChange(event.target.value);
        }}
        onKeyDown={function (event) {
          if (event.nativeEvent.isComposing) return;
          if (event.key === "Escape") {
            setOpen(false);
            setActiveIndex(-1);
          } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            setOpen(true);
            if (!loading && results.length) {
              setActiveIndex(function (previous) {
                return event.key === "ArrowDown"
                  ? (previous + 1) % results.length
                  : (previous <= 0 ? results.length - 1 : previous - 1);
              });
            }
          } else if (event.key === "Enter") {
            event.preventDefault();
            if (showResults && !loading && results[activeIndex]) select(results[activeIndex]);
            else {
              setOpen(false);
              onSearch(value);
            }
          }
        }}
      />
      {showResults && (
        <div className="shop-search__results">
          <div
            id={listId}
            ref={listRef}
            role="listbox"
            aria-label={t("نتایج جست‌وجو")}
            aria-busy={loading}
            className="shop-search__list"
          >
            {!loading && results.map(function (result, index) {
              const master = result.kind === "master";
              return (
                <React.Fragment key={result.key}>
                  {(index === 0 || results[index - 1].kind !== result.kind) && (
                    <div
                      className="shop-search__group"
                      role="presentation"
                    >
                      {master ? t("طرح‌ها") : t("کالاها")}
                    </div>
                  )}
                  <button
                    type="button"
                    id={`${listId}-${index}`}
                    role="option"
                    aria-selected={activeIndex === index}
                    tabIndex={-1}
                    className="shop-search__result"
                    onMouseDown={function (event) { event.preventDefault(); }}
                    onClick={function () { select(result); }}
                  >
                    <span className="shop-search__result-icon">
                      {master ? (
                        <SquaresFour size={20} />
                      ) : (
                        <Tag size={20} />
                      )}
                    </span>
                    <span className="shop-search__result-copy">
                      <strong>{localizeDigits(result.title)}</strong>
                      {resultDescription && (
                        <small>{resultDescription(result)}</small>
                      )}
                      {master ? <small>{t("همهٔ رنگ‌ها و ابعاد")}</small> : null}
                      {master && result.code && (
                        <small>
                          <bdi>{localizeDigits(result.code)}</bdi>
                        </small>
                      )}
                    </span>
                    <span className="shop-search__kind">{master ? t("طرح") : t("کالا")}</span>
                  </button>
                </React.Fragment>
              );
            })}
          </div>
          {(loading || failed || results.length === 0) && (
            <div
              className="shop-search__state"
              role="status"
            >
              {loading ? t("در حال جست‌وجو…") : failed ? t("جست‌وجو انجام نشد؛ دوباره تلاش کنید.") : emptyLabel || t("محصولی یافت نشد")}
            </div>
          )}
          <button
            type="button"
            className="shop-search__all"
            onClick={function () {
              setOpen(false);
              onSearch(value);
            }}
          >
            {allResultsLabel || t("نمایش همهٔ نتایج")}
          </button>
        </div>
      )}
    </div>
  );
}
